<template>
  <span
    v-bind="$attrs"
    class="ui-badge"
    :class="[
      `ui-badge--${color}`,
      `ui-badge--${size}`,
      `ui-badge--${type}`,
      `ui-badge--${variant}`,
      { 'ui-badge--interactive': interactive, 'ui-badge--disabled': disabled },
    ]"
    :role="interactive ? 'button' : undefined"
    :tabindex="interactive && !disabled ? 0 : undefined"
    @click="press"
    @keydown.enter.prevent="press"
    @keydown.space.prevent="press"
  >
    <slot name="icon"><Icon v-if="icon" :name="icon" :size="14" /> </slot>
    <slot>{{ title }}</slot>
    <button
      v-if="$slots.remove || removable"
      aria-label="Usuń"
      data-test-id="ui-badge-remove"
      :disabled="disabled"
      type="button"
      @click.stop="$emit('remove', $event)"
    >
      <slot name="remove"><Icon name="XClose" :size="16" /> </slot>
    </button>
  </span>
</template>

<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue'

  import { Icon, type IconName } from '../Icon'

  defineOptions({ inheritAttrs: false, name: 'UiBadge' })

  const props = withDefaults(
    defineProps<{
      color?: 'blue' | 'blue-light' | 'brand' | 'error' | 'gray' | 'purple' | 'success' | 'warning'
      disabled?: boolean
      icon?: IconName
      removable?: boolean
      size?: 'lg' | 'md' | 'sm'
      title?: string
      type?: 'badge' | 'pill' | 'rounded'
      variant?: 'filled' | 'outline'
    }>(),
    { color: 'gray', size: 'md', type: 'badge', variant: 'filled' },
  )

  const emit = defineEmits<{ press: [event: Event]; remove: [event: MouseEvent] }>()
  const interactive = computed(() => Boolean(getCurrentInstance()?.vnode.props?.onPress))

  const press = (event: Event) => {
    if (!props.disabled) emit('press', event)
  }
</script>

<style scoped lang="scss">
@use '@repo/ui/typography.scss' as typography;

/* Disabled and focus states intentionally take precedence over hover. */
/* stylelint-disable no-descending-specificity */

.ui-badge {
  --background-color: var(--component-color-utility-gray-utility-gray-50);
  --border-color: var(--component-color-utility-gray-utility-gray-200);
  --icon-color: var(--component-color-utility-gray-utility-gray-500);
  --text-color: var(--component-color-utility-gray-utility-gray-700);

  align-items: center;
  border-style: solid;
  border-width: 1px;
  display: inline-flex;
  gap: 4px;
  white-space: nowrap;
}

.ui-badge--blue {
  --background-color: var(--component-color-utility-blue-utility-blue-50);
  --border-color: var(--component-color-utility-blue-utility-blue-200);
  --icon-color: var(--component-color-utility-blue-utility-blue-500);
  --text-color: var(--component-color-utility-blue-utility-blue-700);
}

.ui-badge--blue-light {
  --background-color: var(--component-color-utility-blue-light-utility-blue-light-50);
  --border-color: var(--component-color-utility-blue-light-utility-blue-light-200);
  --icon-color: var(--component-color-utility-blue-light-utility-blue-light-500);
  --text-color: var(--component-color-utility-blue-light-utility-blue-light-700);
}

.ui-badge--brand {
  --background-color: var(--component-color-utility-brand-utility-brand-50);
  --border-color: var(--component-color-utility-brand-utility-brand-200);
  --icon-color: var(--component-color-utility-brand-utility-brand-500);
  --text-color: var(--component-color-utility-brand-utility-brand-700);
}

.ui-badge--success {
  --background-color: var(--component-color-utility-success-utility-success-50);
  --border-color: var(--component-color-utility-success-utility-success-200);
  --icon-color: var(--component-color-utility-success-utility-success-500);
  --text-color: var(--component-color-utility-success-utility-success-700);
}

.ui-badge--warning {
  --background-color: var(--component-color-utility-warning-utility-warning-50);
  --border-color: var(--component-color-utility-warning-utility-warning-200);
  --icon-color: var(--component-color-utility-warning-utility-warning-500);
  --text-color: var(--component-color-utility-warning-utility-warning-700);
}

.ui-badge--purple {
  --background-color: var(--component-color-utility-purple-utility-purple-50);
  --border-color: var(--component-color-utility-purple-utility-purple-200);
  --icon-color: var(--component-color-utility-purple-utility-purple-500);
  --text-color: var(--component-color-utility-purple-utility-purple-700);
}

.ui-badge--error {
  --background-color: var(--component-color-utility-error-utility-error-50);
  --border-color: var(--component-color-utility-error-utility-error-200);
  --icon-color: var(--component-color-utility-error-utility-error-500);
  --text-color: var(--component-color-utility-error-utility-error-700);
}

.ui-badge :deep(svg) {
  color: var(--icon-color);
}

.ui-badge--filled {
  background-color: var(--background-color);
  border-color: var(--border-color);
  color: var(--text-color);
}

.ui-badge--outline {
  background-color: transparent;
  border-color: var(--border-color);
  color: var(--text-color);
}

.ui-badge--sm {
  @include typography.font-style('text-xs', 'medium');

  padding: var(--spacing-xxs) var(--spacing-md);
}

.ui-badge--md {
  @include typography.font-style('text-sm', 'medium');

  padding: var(--spacing-xxs) 10px;
}

.ui-badge--lg {
  @include typography.font-style('text-sm', 'medium');

  padding: var(--spacing-xs) var(--spacing-lg);
}

.ui-badge--badge {
  border-radius: var(--radius-full);
  border-width: 1px;
}

.ui-badge--pill {
  border-width: 1.5px;
}

.ui-badge--sm.ui-badge--pill,
.ui-badge--md.ui-badge--pill {
  border-radius: var(--radius-sm);
}

.ui-badge--lg.ui-badge--pill {
  border-radius: var(--radius-md);
}

.ui-badge--rounded {
  align-items: center;
  border-radius: 100%;
  justify-content: center;
  padding: 0;
}

.ui-badge--sm.ui-badge--rounded {
  height: 20px;
  width: 20px;
}

.ui-badge--md.ui-badge--rounded {
  height: 24px;
  width: 24px;
}

.ui-badge--lg.ui-badge--rounded {
  height: 28px;
  width: 28px;
}

.ui-badge--interactive {
  cursor: pointer;
}

.ui-badge--interactive:hover:not(.ui-badge--disabled) {
  filter: brightness(0.96);
}

.ui-badge--interactive:active:not(.ui-badge--disabled) {
  filter: brightness(0.92);
}

.ui-badge--interactive:focus-visible {
  outline: 2px solid var(--color-effects-focus-rings-focus-ring);
  outline-offset: 2px;
}

.ui-badge--disabled {
  color: var(--color-foreground-fg-disabled);
  cursor: default;
  opacity: 0.65;
}

button {
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  padding: 0;
}

button:focus-visible {
  border-radius: 2px;
  outline: 2px solid currentcolor;
  outline-offset: 1px;
}
</style>
